import React from 'react';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  Text,
  RadioGroup,
  Radio,
  Field,
  Badge,
  InfoButton,
  Card,
} from '@fluentui/react-components';
import {
  Shield24Regular,
  Globe24Regular,
  LockClosed24Regular,
  Warning24Regular,
  CheckmarkCircle24Regular,
} from '@fluentui/react-icons';
import { CommunicationWithExternalOrgs, gfcPolicyService } from '../../services/gfcPolicyService';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  modeOption: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.padding('16px'),
    ...shorthands.border('1px', 'solid', '#e1dfdd'),
    ...shorthands.borderRadius('8px'),
    ...shorthands.gap('12px'),
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f8f9fa',
      ...shorthands.border('1px', 'solid', '#106ebe'),
    },
  },
  selectedMode: {
    backgroundColor: '#f0f8ff',
    ...shorthands.border('1px', 'solid', '#106ebe'),
    boxShadow: '0 2px 4px rgba(16, 110, 190, 0.1)',
  },
  modeHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modeTitle: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  modeDetails: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
  },
  riskBadge: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
  },
  useCase: {
    fontStyle: 'italic',
    color: '#605e5c',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
  },
  modeCard: {
    ...shorthands.margin('4px', '0'),
    ...shorthands.border('1px', 'solid', '#e1dfdd'),
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f8f9fa',
      ...shorthands.border('1px', 'solid', '#106ebe'),
    },
  },
  selectedCard: {
    backgroundColor: '#f0f8ff',
    ...shorthands.border('1px', 'solid', '#106ebe'),
    boxShadow: '0 2px 4px rgba(16, 110, 190, 0.1)',
  },
});

interface CommunicationModeSelectorProps {
  value: CommunicationWithExternalOrgs;
  onChange: (mode: CommunicationWithExternalOrgs) => void;
  disabled?: boolean;
  showDescription?: boolean;
}

export const CommunicationModeSelector: React.FC<CommunicationModeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  showDescription = true,
}) => {
  const styles = useStyles();
  const modeDescriptions = gfcPolicyService.getCommunicationModeDescriptions();

  const getModeIcon = (mode: CommunicationWithExternalOrgs) => {
    switch (mode) {
      case 'OrganizationDefault':
        return <Shield24Regular />;
      case 'AllowAllExternalDomains':
        return <Globe24Regular style={{ color: '#d13438' }} />;
      case 'AllowSpecificExternalDomains':
        return <CheckmarkCircle24Regular style={{ color: '#107C10' }} />;
      case 'BlockSpecificExternalDomains':
        return <Warning24Regular style={{ color: '#ff8c00' }} />;
      case 'BlockAllExternalDomains':
        return <LockClosed24Regular style={{ color: '#005a9e' }} />;
    }
  };

  const getRiskBadgeProps = (riskLevel: 'low' | 'medium' | 'high') => {
    switch (riskLevel) {
      case 'low':
        return { appearance: 'filled' as const, color: 'success' as const };
      case 'medium':
        return { appearance: 'filled' as const, color: 'warning' as const };
      case 'high':
        return { appearance: 'filled' as const, color: 'danger' as const };
    }
  };

  const getDetailedDescription = (mode: CommunicationWithExternalOrgs) => {
    const modeInfo = modeDescriptions[mode];
    switch (mode) {
      case 'OrganizationDefault':
        return (
          <div data-testid="gfc-communication-mode-org-default-details">
            <Text size={200} data-testid="gfc-communication-mode-org-default-description">
              This policy will inherit your organization's current Teams federation settings. 
              If your organization changes its default settings, this policy will automatically 
              follow those changes.
            </Text>
            <br data-testid="gfc-communication-mode-org-default-break" />
            <Text size={200} weight="semibold" data-testid="gfc-communication-mode-org-default-best-for-label">Best for:</Text>
            <Text size={200} data-testid="gfc-communication-mode-org-default-best-for-text"> Organizations with established governance policies that want consistency across all policies.</Text>
          </div>
        );
      case 'AllowAllExternalDomains':
        return (
          <div data-testid="gfc-communication-mode-allow-all-details">
            <Text size={200} data-testid="gfc-communication-mode-allow-all-description">
              <strong data-testid="gfc-communication-mode-allow-all-warning">⚠️ High Risk:</strong> This allows communication with any external domain, 
              including potentially malicious or untrusted domains. Users can communicate 
              with anyone outside your organization.
            </Text>
            <br data-testid="gfc-communication-mode-allow-all-break" />
            <Text size={200} weight="semibold" data-testid="gfc-communication-mode-allow-all-best-for-label">Best for:</Text>
            <Text size={200} data-testid="gfc-communication-mode-allow-all-best-for-text"> Research organizations or consulting firms that need maximum flexibility for client engagement.</Text>
          </div>
        );
      case 'AllowSpecificExternalDomains':
        return (
          <div data-testid="gfc-communication-mode-allow-specific-details">
            <Text size={200} data-testid="gfc-communication-mode-allow-specific-description">
              <strong data-testid="gfc-communication-mode-allow-specific-recommendation">✅ Recommended for Financial Services:</strong> Only domains you explicitly 
              allow will be able to communicate with your users. This provides the highest 
              security by blocking all domains except those you trust.
            </Text>
            <br data-testid="gfc-communication-mode-allow-specific-break" />
            <Text size={200} weight="semibold" data-testid="gfc-communication-mode-allow-specific-best-for-label">Best for:</Text>
            <Text size={200} data-testid="gfc-communication-mode-allow-specific-best-for-text"> Banks, financial institutions, and organizations with known, trusted external partners.</Text>
          </div>
        );
      case 'BlockSpecificExternalDomains':
        return (
          <div data-testid="gfc-communication-mode-block-specific-details">
            <Text size={200} data-testid="gfc-communication-mode-block-specific-description">
              All external domains are allowed except those you specifically block. This is 
              useful when you know specific domains that pose risks but want to maintain 
              general openness to external collaboration.
            </Text>
            <br data-testid="gfc-communication-mode-block-specific-break" />
            <Text size={200} weight="semibold" data-testid="gfc-communication-mode-block-specific-best-for-label">Best for:</Text>
            <Text size={200} data-testid="gfc-communication-mode-block-specific-best-for-text"> Organizations that want to block known bad actors while staying open to new partners.</Text>
          </div>
        );
      case 'BlockAllExternalDomains':
        return (
          <div data-testid="gfc-communication-mode-block-all-details">
            <Text size={200} data-testid="gfc-communication-mode-block-all-description">
              <strong data-testid="gfc-communication-mode-block-all-security">🔒 Maximum Security:</strong> No external domain communication is allowed. 
              Users can only communicate with people within your organization. This provides 
              the highest security but eliminates external collaboration capabilities.
            </Text>
            <br data-testid="gfc-communication-mode-block-all-break" />
            <Text size={200} weight="semibold" data-testid="gfc-communication-mode-block-all-best-for-label">Best for:</Text>
            <Text size={200} data-testid="gfc-communication-mode-block-all-best-for-text"> Government agencies, highly regulated industries, or internal-only organizations.</Text>
          </div>
        );
    }
  };

  return (
    <Field
      data-testid="gfc-communication-mode-field"
      label={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="gfc-communication-mode-label-row">
          <Text weight="semibold" data-testid="gfc-communication-mode-label">Communication Mode</Text>
          <InfoButton
            info={
              <div style={{ maxWidth: '400px' }} data-testid="gfc-communication-mode-info-content">
                <Text size={200} data-testid="gfc-communication-mode-info-text">
                  <strong data-testid="gfc-communication-mode-info-title">GFC Communication Modes</strong><br data-testid="gfc-communication-mode-info-break" />
                  Choose how this policy handles external domain communication. This setting 
                  determines which external domains can communicate with users assigned to this policy.
                </Text>
              </div>
            }
          />
        </div>
      }
      required
    >
      <div className={styles.container} data-testid="gfc-communication-mode-selector">
        {showDescription && (
          <Text size={300} style={{ color: '#605e5c', marginBottom: '8px' }} data-testid="gfc-communication-mode-instructions">
            Select how users assigned to this policy can communicate with external domains:
          </Text>
        )}

        <RadioGroup
          value={value}
          onChange={(_, data) => onChange(data.value as CommunicationWithExternalOrgs)}
          disabled={disabled}
          className={styles.radioGroup}
          data-testid="gfc-communication-mode-options"
        >
          {(Object.keys(modeDescriptions) as CommunicationWithExternalOrgs[]).map((mode, modeIndex) => {
            const description = modeDescriptions[mode];
            const isSelected = value === mode;
            
            return (
              <div key={mode} data-testid={`gfc-communication-mode-option-container-${modeIndex}`}>
                <Radio
                  value={mode}
                  data-testid={`gfc-communication-mode-option-${modeIndex}`}
                  label={
                    <Card 
                      className={mergeClasses(
                        styles.modeCard,
                        isSelected && styles.selectedCard
                      )}
                      style={{ width: '100%', cursor: 'pointer' }}
                      data-testid={`gfc-communication-mode-card-${modeIndex}`}
                    >
                      <div style={{ padding: '12px' }} data-testid={`gfc-communication-mode-card-content-${modeIndex}`}>
                        <div className={styles.modeHeader} data-testid={`gfc-communication-mode-card-header-${modeIndex}`}>
                          <div className={styles.modeTitle} data-testid={`gfc-communication-mode-card-title-${modeIndex}`}>
                            {getModeIcon(mode)}
                            <Text weight="semibold" size={400} data-testid={`gfc-communication-mode-title-${modeIndex}`}>
                              {description.title}
                            </Text>
                          </div>
                          <div className={styles.riskBadge} data-testid={`gfc-communication-mode-risk-badge-${modeIndex}`}>
                            <Badge {...getRiskBadgeProps(description.riskLevel)} data-testid={`gfc-communication-mode-risk-badge-value-${modeIndex}`}>
                              {description.riskLevel.toUpperCase()} RISK
                            </Badge>
                          </div>
                        </div>
                        
                        <div className={styles.modeDetails} data-testid={`gfc-communication-mode-card-details-${modeIndex}`}>
                          <Text size={300} data-testid={`gfc-communication-mode-description-${modeIndex}`}>
                            {description.description}
                          </Text>
                          <Text size={200} className={styles.useCase} data-testid={`gfc-communication-mode-use-case-${modeIndex}`}>
                            {description.useCase}
                          </Text>
                        </div>
                      </div>
                    </Card>
                  }
                />
                
                {isSelected && (
                  <div style={{ 
                    marginLeft: '24px', 
                    marginTop: '8px', 
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #e1dfdd'
                  }} data-testid={`gfc-communication-mode-details-${modeIndex}`}>
                    {getDetailedDescription(mode)}
                  </div>
                )}
              </div>
            );
          })}
        </RadioGroup>
      </div>
    </Field>
  );
};